import { useTranslation } from "react-i18next";
import { CoachObjectiveMetricPicker } from "./CoachObjectiveMetricPicker";
import "./ProfileSmartTab.css";

function trafficChipClass(onTrack) {
  if (onTrack === true) return "pst-chip--ok";
  if (onTrack === false) return "pst-chip--warn";
  return "pst-chip--now";
}

function tierChipClass(tier) {
  return tier === "premium" ? "pst-chip--ai" : "pst-chip--sec";
}

function formatMetricValue(value) {
  if (value == null || Number.isNaN(Number(value))) return "—";
  const n = Number(value);
  if (Math.abs(n) >= 100) return String(Math.round(n));
  if (Math.abs(n) >= 10) return n.toFixed(1);
  return n.toFixed(2);
}

function ExerciseMRow({ row, t, userId, coachView = false }) {
  const roleMark = row.goalRole === "secondary" ? "·" : "★";
  return (
    <tr className={`pst-sprint-row${row.on_track === true ? " is-ok" : row.on_track === false ? " is-warn" : ""}`}>
      <td className="pst-sprint-row__goal">
        <span className="pst-sprint-row__role" aria-hidden="true">{roleMark}</span>
        <div>
          <strong>{row.title}</strong>
          {row.goalLabel ? <span className="pst-muted pst-sprint-row__sub">{row.goalLabel}</span> : null}
        </div>
      </td>
      <td>{row.cb_name || "—"}</td>
      <td>
        <span className={`pst-chip ${tierChipClass(row.m_tier)}`} title={row.m_tier_label}>
          {row.m_tier === "premium" ? t("smartMPlanPremium") : t("smartMPlanBase")}
        </span>
      </td>
      <td>
        {row.autoeval?.latest != null ? (
          <span className={row.autoeval.pass ? "pst-sprint-ok" : "pst-sprint-warn"}>
            {row.autoeval.latest}/5
            {row.autoeval.avg != null && row.autoeval.count > 1 ? (
              <span className="pst-muted"> · ø{row.autoeval.avg}</span>
            ) : null}
          </span>
        ) : (
          <span className="pst-muted">{t("smartNotPerformedYet")}</span>
        )}
      </td>
      <td>
        {row.premium_configured ? (
          row.premium_metrics?.length ? (
            <ul className="pst-sprint-metrics">
              {row.premium_metrics.map((m) => (
                <li key={m.kind}>
                  <span className="pst-sprint-metrics__label">{m.label}</span>
                  <span className={m.latest != null ? "pst-sprint-ok" : "pst-muted"}>
                    {formatMetricValue(m.latest)}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <span className="pst-muted">{t("smartNoPremiumMetrics")}</span>
          )
        ) : (
          <span className="pst-muted">{t("smartPremiumNotConfigured")}</span>
        )}
      </td>
      <td>
        <span className={`pst-chip ${trafficChipClass(row.on_track)}`}>
          {row.on_track === true
            ? t("smartOnTrack")
            : row.on_track === false
              ? t("smartBehindPlan")
              : t("smartPendingData")}
        </span>
      </td>
      {coachView && userId && row.exerciseId ? (
        <td className="pst-sprint-row__edit">
          <CoachObjectiveMetricPicker
            playerUserId={userId}
            exId={row.exerciseId}
            goalRole={row.goalRole || "primary"}
            goalLabel={row.goalLabel}
            mode="edit"
            compact
          />
        </td>
      ) : null}
    </tr>
  );
}

/**
 * Sprint (3–4 week) exercise progress: CB → drill → base autoeval + coach premium metrics.
 */
export function SprintExerciseTable({
  sprintProgress,
  userId = null,
  coachView = false,
  compact = false,
}) {
  const { t } = useTranslation();
  if (!sprintProgress?.exercises?.length) {
    return (
      <p className="pst-empty-copy">{t("smartSprintExercisesEmpty")}</p>
    );
  }

  const cycle = sprintProgress.cycle || {};
  const summary = sprintProgress.summary || {};
  const plan = sprintProgress.measurementPlan || "standard";

  return (
    <section className={`pst-sprint-table-wrap${compact ? " is-compact" : ""}`}>
      <header className="pst-sprint-table__head">
        <div>
          <h3 className="pst-h">{t("smartSprintExerciseBoard")}</h3>
          <p className="pst-muted">
            {t("smartSprintCycleHint", {
              weeks: cycle.weeks ?? 3,
              start: cycle.cycleStart || "—",
              end: cycle.cycleEnd || "—",
            })}
          </p>
        </div>
        <div className="pst-sprint-table__badges">
          <span className={`pst-chip ${plan === "premium" ? "pst-chip--ai" : "pst-chip--sec"}`}>
            {plan === "premium" ? t("smartMPlanPremium") : t("smartMPlanBase")}
          </span>
          {summary.on_track != null ? (
            <span className="pst-chip pst-chip--ok">
              {t("smartSprintOnTrackCount", {
                on: summary.on_track,
                total: summary.total,
              })}
            </span>
          ) : null}
        </div>
      </header>

      {cycle.presence?.present_sessions != null ? (
        <p className="pst-sprint-presence pst-muted">
          {t("playerPathSprintPresence", {
            present: cycle.presence.present_sessions,
            expected: cycle.presence.expected_to_date ?? "—",
            defaultValue: `${cycle.presence.present_sessions}/${cycle.presence.expected_to_date ?? "—"} sprint sessions`,
          })}
        </p>
      ) : null}

      <div className="pst-sprint-table-scroll">
        <table className="pst-sprint-table">
          <thead>
            <tr>
              <th>{t("smartSubObjective")}</th>
              <th>{t("smartCompetencyBlock")}</th>
              <th>{t("smartMPlanColumn")}</th>
              <th>{t("autoeval")}</th>
              <th>{t("smartPremiumMetricsColumn")}</th>
              <th>{t("smartProgressScore")}</th>
              {coachView ? <th>{t("smartEditMetrics")}</th> : null}
            </tr>
          </thead>
          <tbody>
            {sprintProgress.exercises.map((row) => (
              <ExerciseMRow
                key={`${row.exerciseId}-${row.goalRole}`}
                row={row}
                t={t}
                userId={userId}
                coachView={coachView}
              />
            ))}
          </tbody>
        </table>
      </div>

      {!compact && sprintProgress.byCompetencyBlock?.length > 1 ? (
        <div className="pst-sprint-cb-groups">
          {sprintProgress.byCompetencyBlock.map((group) => (
            <details key={group.cb_id} className="pst-sprint-cb-group">
              <summary>
                {group.cb_name || t("smartUnclassified")} ({group.exercises.length})
              </summary>
              <ul className="pst-sprint-cb-list">
                {group.exercises.map((ex) => (
                  <li key={ex.exerciseId}>
                    {ex.title}
                    {ex.autoeval?.latest != null ? ` · ${ex.autoeval.latest}/5` : ""}
                  </li>
                ))}
              </ul>
            </details>
          ))}
        </div>
      ) : null}
    </section>
  );
}

export default SprintExerciseTable;
